/**
 * SE3 Reg D tombstone/spotlight legal disclosure copy.
 *
 * The tombstone feed does NOT carry the underlying portfolio company's name
 * (`startengine_company_name` is the broker-dealer "StartEngine Primary, LLC",
 * and `name` is the series display name) — so single-company series use generic
 * "the underlying company" wording rather than naming it. Multi-company funds
 * ("… a series of StartEngine Private Funds LLC") describe a portfolio: you buy
 * the fund, not the underlying companies (Paige QA, 2026-07-08).
 */

/**
 * Regulation classification.
 *
 * The tombstone/elastic feeds are not consistent about how they spell the
 * regulation ("Reg A+", "RegA", "Reg A Tier 2", "Regulation A", "506(c)", …).
 * Exact-string Set matching (the previous approach, duplicated and already
 * drifting between elasticExplore and useHomepageV3Offerings) let unrecognized
 * spellings fall through, so a Reg A+/Reg CF card silently rendered the Reg D
 * "membership interest" disclosure (Paige QA 2026-07-15). Normalize once and
 * classify by prefix so every spelling routes to the correct disclosure.
 */
const normalizeReg = (reg?: string): string =>
  (reg ?? '')
    .toLowerCase()
    .replace(/regulation/g, 'reg')
    .replace(/[^a-z0-9]/g, '');

export const isRegCf = (reg?: string): boolean => {
  const n = normalizeReg(reg);
  return n.startsWith('regcf') || n === 'regc' || n.includes('crowdfunding') || n === '4a6';
};

export const isRegA = (reg?: string): boolean => normalizeReg(reg).startsWith('rega');

export const isRegD = (reg?: string): boolean => {
  const n = normalizeReg(reg);
  return n.startsWith('regd') || /^506[cb]/.test(n);
};

const entityPhrase = (companyName?: string): string => {
  const raw = (companyName ?? '').trim().replace(/^Series\s+Series\b/i, 'Series');
  return raw || 'this series, a series of StartEngine Private LLC';
};

export function regDDisclosure(companyName?: string): string {
  const entity = entityPhrase(companyName);
  if (/private funds llc/i.test(entity)) {
    return `This offering is for membership interest in ${entity}, which holds interests in a portfolio of underlying companies. You are not buying those companies' shares directly.`;
  }
  return `This offering is for membership interest in ${entity}, which will own interest in the underlying company. You are not buying the underlying company's shares directly.`;
}
